import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { BellIcon } from '@heroicons/react/24/outline';
import { useNotificaciones } from '../hooks/useNotificaciones';
import { marcarNotificacionLeida, marcarNotificacionesLeidas } from '../hooks/useOrdenesTrabajo';
import { folioTexto } from '../utils/ordenesTrabajo';

const hace = (ms, nowMs = Date.now()) => {
  const diff = Math.max(0, nowMs - Number(ms || 0));
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'ahora';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return `hace ${d} d`;
};

/**
 * The bell: my OT notifications, unread count on the icon, a panel with the
 * list. Clicking a row marks it read and opens the OT. In-app only for now;
 * email per equipo comes later through functions.
 *
 * Mounted once in Dashboard (sidebar, brokers, orgs with OTs on). `oscura`
 * draws it for the dark sidebar; the panel itself is always light.
 *
 * @param {Object} props
 * @param {boolean} props.activo
 * @param {boolean} [props.compacto]  - collapsed sidebar: icon only
 */
export function CampanaNotificaciones({ activo, compacto = false }) {
  const navigate = useNavigate();
  const { rows, noLeidas } = useNotificaciones(activo);
  const [abierta, setAbierta] = useState(false);
  const ref = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!abierta) return undefined;
    const cerrar = (e) => {
      if (ref.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setAbierta(false);
    };
    document.addEventListener('mousedown', cerrar);
    return () => document.removeEventListener('mousedown', cerrar);
  }, [abierta]);

  if (!activo) return null;

  const abrir = async (n) => {
    setAbierta(false);
    if (!n.leida) {
      try { await marcarNotificacionLeida(n.id); } catch (e) { console.error('marcar leida', e); }
    }
    if (n.otId) navigate(`/app/ordenes?ot=${n.otId}`);
  };

  const todasLeidas = async () => {
    try { await marcarNotificacionesLeidas(noLeidas.map((n) => n.id)); } catch (e) { console.error('marcar leidas', e); }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setAbierta((v) => !v)}
        title="Notificaciones"
        className={clsx(
          'relative flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-base/6 font-medium text-white sm:py-2 sm:text-sm/5 hover:bg-white/10',
          compacto && 'justify-center',
        )}
      >
        <span className="relative inline-flex">
          <BellIcon className="w-5 h-5" />
          {noLeidas.length > 0 && (
            <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-verde-lima text-azul-surelia text-[10px] font-bold leading-4 text-center tabular-nums">
              {noLeidas.length > 99 ? '99+' : noLeidas.length}
            </span>
          )}
        </span>
        {!compacto && <span className="truncate">Notificaciones</span>}
      </button>

      {/* Portaled to body: inside the sidebar the panel inherits the
          sidebar's stacking context and the main area's popovers (asignados
          bubbles, dialogs) paint over it whatever z-index it carries. */}
      {abierta && createPortal(
        <div ref={panelRef} className={clsx(
          'fixed bottom-4 top-auto z-[200] w-[22rem] max-h-[70vh] flex flex-col rounded-xl border border-zinc-200 bg-white shadow-2xl text-zinc-800 max-lg:left-4 max-lg:right-4 max-lg:w-auto',
          compacto ? 'left-[4.5rem]' : 'left-[17rem]',
        )}>
          <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-100">
            <span className="text-sm font-semibold text-azul-surelia">Notificaciones</span>
            {noLeidas.length > 0 && (
              <button onClick={todasLeidas} className="text-xs text-zinc-500 hover:text-azul-surelia">
                Marcar todas leídas
              </button>
            )}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {rows.length === 0 ? (
              <div className="px-3 py-8 text-center text-xs text-zinc-400">Nada por ahora.</div>
            ) : (
              <ul className="divide-y divide-zinc-100">
                {rows.map((n) => (
                  <li key={n.id}>
                    <button
                      onClick={() => abrir(n)}
                      className={clsx('w-full text-left px-3 py-2 hover:bg-zinc-50', !n.leida && 'bg-azul-surelia/5')}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-xs font-semibold tabular-nums text-azul-surelia">{folioTexto(n.folio)}</span>
                        <span className="text-[11px] text-zinc-400 whitespace-nowrap">{hace(n.createdAt)}</span>
                      </div>
                      <div className={clsx('text-sm leading-snug', n.leida ? 'text-zinc-600' : 'text-zinc-800')}>{n.texto}</div>
                      {n.titulo && <div className="text-xs text-zinc-400 truncate">{n.titulo}</div>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default CampanaNotificaciones;
